import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import { SurveyItem, SurveyConclusion } from "../components/survey/SurveyItem";
import { SurveyMediaItem } from "../components/survey/SurveyMediaItem";
import { SurveyOverview } from "../components/survey/SurveyOverview";
import { EvidenceLightbox } from "../components/EvidenceLightbox";
import { ArrowLeft, CheckCircle2, FileDown, Loader2, Save } from "lucide-react";
import { exportSurveyPdf } from "../components/survey/exportSurveyPdf";

import { T as TI } from "../lib/i18n";
const ST_DOT = { pending: "bg-slate-300", draft: "bg-sky-500", submitted: "bg-emerald-500" };
const mergeInto = (base, partial) => {
  const out = { ...base };
  Object.entries(partial).forEach(([k, v]) => { out[k] = v && typeof v === "object" && !Array.isArray(v) ? { ...(out[k] || {}), ...v } : v; });
  return out;
};

/* PM 现场踏勘风险清单：手机友好的全屏分步表单（5 页分别保存 / 提交，可中断续填）
 *
 * 两种形态（2026-10-15 抽屉化改造）：整页态（路由 /project/:id/site-survey）
 * 与 embedded（GlobalProjectDrawer 第 1 层抽屉，id 走 projectId prop、返回走 onClose）。
 * embedded 时底部提交栏从 fixed 改 sticky —— fixed 相对视口定位会溢出抽屉面板，
 * sticky 则贴在抽屉滚动容器底部（CrmDetailDrawer 的 body 就是滚动容器）。
 */
export default function SiteSurvey({ embedded = false, projectId: propId, onClose } = {}) {
  const { id: routeId } = useParams();
  const id = propId || routeId;
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const T = (a, b) => (zh ? a : b);
  const page = sp.get("page") || "overview";
  const [def, setDef] = useState(null);
  const [st, setSt] = useState(null);
  const [proj, setProj] = useState(null);
  const [missing, setMissing] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const pendingRef = useRef({ key: null, body: {} });
  const chainRef = useRef(Promise.resolve());
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data: d }, { data: s }] = await Promise.all([api.get("/site-survey/checklist"), api.get(`/projects/${id}/site-survey`)]);
    setDef(d); setSt(s); setProj(s?.project || null);
  }, [api, id]);
  useEffect(() => { load().catch((e) => toast.error(e?.response?.data?.detail || T("加载失败", "Load failed"))); }, [load]); // eslint-disable-line
  useEffect(() => { setMissing([]); window.scrollTo({ top: 0 }); }, [page]);

  const spec = useMemo(() => def?.pages.find((p) => p.key === page), [def, page]);
  const pg = st?.pages?.[page] || {};
  const canFill = !!st?.canFill && !!user;

  const flush = useCallback(() => {
    const run = async () => {
      const { key, body } = pendingRef.current;
      pendingRef.current = { key: null, body: {} };
      if (!key || !Object.keys(body).length) return;
      setSaving(true);
      try { const { data } = await api.put(`/projects/${id}/site-survey/pages/${key}`, body); setSt((s) => ({ ...s, summary: data.summary })); }
      catch (e) {
        if (pendingRef.current.key === key || !pendingRef.current.key) pendingRef.current = { key, body: mergeInto(body, pendingRef.current.body) };
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || T("保存失败", "Save failed"));
      } finally { setSaving(false); }
    };
    chainRef.current = chainRef.current.then(run, run);
    return chainRef.current;
  }, [api, id]); // eslint-disable-line
  const patch = (partial) => {
    setSt((s) => ({ ...s, pages: { ...s.pages, [page]: mergeInto(s.pages?.[page] || {}, partial) } }));
    if (pendingRef.current.key && pendingRef.current.key !== page) flush();
    pendingRef.current = { key: page, body: mergeInto(pendingRef.current.body, partial) };
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 700);
  };
  useEffect(() => () => { clearTimeout(timerRef.current); }, []);
  const go = (p) => { clearTimeout(timerRef.current); flush(); setSp(p === "overview" ? {} : { page: p }); };

  const submit = async () => {
    clearTimeout(timerRef.current);
    setSubmitting(true); setMissing([]);
    try {
      await flush();
      const { data } = await api.post(`/projects/${id}/site-survey/pages/${spec.key}/submit?lang=${zh ? "zh" : "en"}`, {});
      setSt((s) => ({ ...s, pages: { ...s.pages, [spec.key]: data.page }, summary: data.summary }));
      toast.success(spec.key === "photo_video" ? T("照片/视频清单已提交，五页清单完成", "Photo/Video submitted") : T(`${TI(spec.name.zh)} 已提交`, `${TI(spec.name.en)} submitted`));
      const rs = data.reportSent;
      if (rs) toast.success(rs.dryRun ? T(`测试项目：报告 v${rs.version} 已生成（未真实发送邮件）`, `Test project: report v${rs.version} generated (no email sent)`) : T(`踏勘报告${rs.version > 1 ? `更新版 v${rs.version}` : ""}已自动发送设计部 ${rs.recipients.filter((r) => r.kind === "to").length} 人${rs.recipients.some((r) => r.kind === "cc") ? "，抄送 PM" : ""}`, `Report ${rs.version > 1 ? `v${rs.version} ` : ""}emailed to design team (${rs.recipients.length})`), { duration: 6000 });
      if (rs || data.summary?.done) load().catch(() => {});
      go(data.nextPage && data.nextPage !== spec.key ? data.nextPage : "overview");
    } catch (e) {
      const d = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
      const miss = d?.missing || [];
      setMissing(miss);
      toast.error(d?.message || (typeof d === "string" ? d : T("提交失败", "Submit failed")));
      const first = miss[0];
      const el = first && document.querySelector(first === "CONCLUSION" ? "[data-testid='survey-conclusion']" : first === "DEMO" ? "[data-testid='survey-demo']" : `[data-testid='survey-item-${first}']`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    } finally { setSubmitting(false); }
  };

  const [resendBusy, setResendBusy] = useState(false);
  const resend = async () => {
    if (!window.confirm(T("重新生成踏勘报告并发送给设计部（抄送 PM）？", "Regenerate the report and email the design team (cc PM)?"))) return;
    setResendBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/site-survey/report/send`, {});
      toast.success(data.dryRun ? T(`测试项目：报告 v${data.version} 已生成（未真实发送）`, `Test project: v${data.version} generated (no email)`) : T(`报告 v${data.version} 已发送 ${data.sent} 人${data.failed ? `，${data.failed} 封失败` : ""}`, `Report v${data.version} sent to ${data.sent}${data.failed ? `, ${data.failed} failed` : ""}`));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || T("发送失败", "Send failed")); }
    finally { setResendBusy(false); }
  };

  if (!def || !st) return <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2" data-testid="survey-loading"><Loader2 className="h-4 w-4 animate-spin" />{T("加载清单…", "Loading…")}</div>;
  const summary = st.summary;
  const isMedia = spec?.kind === "media";
  return (
    <div className="max-w-2xl mx-auto pb-28" data-testid="site-survey-page">
      <div className="sticky top-0 z-20 -mx-2 px-2 pt-1 pb-2 bg-slate-50/95 backdrop-blur border-b border-slate-200 space-y-2">
        <div className="flex items-center gap-2">
          {/* 返回：整页态回项目详情并定位踏勘任务（原行为）；抽屉态关本层抽屉，
              由 GlobalProjectDrawer 负责在 URL 上补 task=WF-01-02-01 供 ProjectHub 读取 */}
          <button type="button" onClick={() => { clearTimeout(timerRef.current); flush(); if (embedded && onClose) onClose(); else nav(`/project/${id}?task=WF-01-02-01`); }} className="p-1.5 rounded-lg hover:bg-slate-200 inline-flex items-center gap-1 text-xs text-slate-600" data-testid="survey-back" aria-label="back" title={T("返回上一步：现场踏勘采集", "Back to site survey task")}><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">{T("返回踏勘采集", "Back")}</span></button>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900 truncate">{zh ? def.title.zh : def.title.en}</p>
            <p className="text-[11px] text-slate-500 truncate" data-testid="survey-project">{proj?.address || id}</p>
          </div>
          {spec && <span className="text-xs font-semibold text-slate-600 shrink-0" data-testid="survey-page-no">{spec.order} / 5</span>}
          <button type="button" disabled={pdfBusy} onClick={() => exportSurveyPdf(api, id, zh, setPdfBusy, lang)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-600 disabled:opacity-50" data-testid="survey-export-pdf" title={T("导出踏勘报告 PDF（自动存入项目文件）", "Export PDF report")}>{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}</button>
          {saving && <Save className="h-3.5 w-3.5 text-slate-400 animate-pulse" data-testid="survey-saving" />}
        </div>
        <div className="flex gap-1 overflow-x-auto no-scrollbar" data-testid="survey-tabs">
          <button type="button" onClick={() => go("overview")} className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full border ${page === "overview" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid="survey-tab-overview">{T("总览", "Overview")}</button>
          {summary.pages.map((p) => (
            <button key={p.key} type="button" onClick={() => go(p.key)} className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${page === p.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`survey-tab-${p.key}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${ST_DOT[p.status]}`} />{zh ? p.name.zh : p.name.en}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-3 space-y-3">
        {!spec && <SurveyOverview def={def} summary={summary} zh={zh} canFill={canFill} onGo={go} riskTasks={st.riskTasks || []} onExport={() => exportSurveyPdf(api, id, zh, setPdfBusy, lang)} pdfBusy={pdfBusy} reportSend={st.reportSend} onResend={resend} resendBusy={resendBusy} />}
        {spec && (
          <>
            <div className="rounded-xl bg-slate-900 text-white p-4" data-testid="survey-page-head">
              <h1 className="text-base sm:text-lg font-bold leading-snug" data-keep-title>{zh ? spec.title.zh : spec.title.en}</h1>
              <p className="text-xs text-slate-300 mt-1 leading-relaxed">{zh ? spec.desc.zh : spec.desc.en}</p>
              {pg.submittedAt && <p className="text-[10px] text-emerald-300 mt-2 inline-flex items-center gap-1" data-testid="survey-page-submitted"><CheckCircle2 className="h-3 w-3" />{T("已提交", "Submitted")} · {pg.submittedBy} · {new Date(pg.submittedAt).toLocaleString()}{T("（可修改后重新提交）", " (edit & resubmit allowed)")}</p>}
            </div>
            {!isMedia && spec.items.map((it, i) => (
              <SurveyItem key={it.code} item={it} idx={i} zh={zh} value={(pg.answers || {})[it.code]} missing={missing.includes(it.code)} onChange={(v) => canFill && patch({ answers: { [it.code]: v } })} />
            ))}
            {!isMedia && <SurveyConclusion page={spec} levels={def.levels} zh={zh} value={pg.conclusion} missing={missing.includes("CONCLUSION")} onChange={(v) => canFill && patch({ conclusion: v })} />}
            {isMedia && spec.items.map((it) => (
              <SurveyMediaItem key={it.code} api={api} pid={id} item={it} pg={pg} zh={zh} missing={missing.includes(it.code)} onPreview={setPreview}
                onFiles={(code, files) => setSt((s) => ({ ...s, pages: { ...s.pages, photo_video: { ...(s.pages?.photo_video || {}), files: { ...((s.pages?.photo_video || {}).files || {}), [code]: files } } } }))}
                onField={(k, code, v) => canFill && patch({ [k]: { [code]: v } })} />
            ))}
            {isMedia && (
              <div className={`rounded-xl border bg-white p-3 sm:p-4 space-y-2 ${missing.includes("DEMO") ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"}`} data-testid="survey-demo">
                <p className="text-sm font-bold text-slate-900">{zh ? spec.demo.title.zh : spec.demo.title.en}</p>
                <p className="text-xs text-slate-500">{zh ? spec.demo.desc.zh : spec.demo.desc.en}</p>
                <div className="space-y-1.5" role="radiogroup">
                  {spec.demo.options.map((o) => (
                    <button key={o.key} type="button" role="radio" aria-checked={pg.demo === o.key} onClick={() => canFill && patch({ demo: o.key })} data-testid={`survey-demo-${o.key}`}
                      className={`w-full text-left rounded-lg border-2 px-3 py-2.5 ${pg.demo === o.key ? (o.key === "needed" ? "border-red-400 bg-red-50" : "border-emerald-400 bg-emerald-50") : "border-slate-200 hover:border-slate-400"}`}>
                      <p className="text-xs font-semibold text-slate-900">{zh ? o.label.zh : o.label.en}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{zh ? o.desc.zh : o.desc.en}</p>
                    </button>
                  ))}
                </div>
                {missing.includes("DEMO") && <p className="text-[10px] text-red-600" data-testid="survey-missing-DEMO">{T("请选择 Demolition 判断", "Please choose the demolition decision")}</p>}
                <p className="text-[10px] text-slate-400">{T("「需要 demolition」提交后将自动完成工程链拆除判定；「无需 demolition」需再按流程上传 Land Survey / 业主豁免文件完成正式判定。", "“Needed” auto-applies the engine demo decision; “none” still requires the survey/waiver upload for the formal decision.")}</p>
              </div>
            )}
            <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 space-y-1.5" data-testid="survey-note-box">
              <p className="text-sm font-bold text-slate-900">{T("备注 / 现场未决条件", "Notes / unresolved field conditions")}</p>
              <textarea rows={3} value={pg.note || ""} onChange={(e) => canFill && patch({ note: e.target.value })} className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2" data-testid="survey-note" placeholder={T("可选", "Optional")} />
            </div>
          </>
        )}
      </div>

      {spec && canFill && (
        <div className={`${embedded ? "sticky bottom-0" : "fixed bottom-0 inset-x-0"} z-30 p-3 bg-white/95 backdrop-blur border-t border-slate-200`}>
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <span className="text-[10px] text-slate-400 shrink-0" data-testid="survey-autosave-hint">{saving ? T("保存中…", "Saving…") : T("已自动保存草稿", "Draft auto-saved")}</span>
            <button type="button" onClick={submit} disabled={submitting} className="flex-1 rounded-xl bg-amber-600 text-white text-sm font-semibold py-3 hover:bg-amber-700 disabled:opacity-60 inline-flex items-center justify-center gap-1" data-testid="survey-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{zh ? spec.submit.zh : spec.submit.en}
            </button>
          </div>
        </div>
      )}
      <EvidenceLightbox ev={preview} zh={zh} onClose={() => setPreview(null)} items={preview ? [preview] : []} onNav={setPreview} />
    </div>
  );
}
